import { useState } from 'react';
import TableContainer from '../../../components/table-container/table-container';
import { TableHead } from '../../../components/table-head';
import {
  useArchiveAnnouncementsMutation,
  useGetAllAnnouncementsQuery,
  useMarkAnnouncementAsReadByMatchIdMutation,
} from '../../../slices/inboxApiSlice';
import { useNavigate } from 'react-router';
import TableTd from '../../../components/table-td/table-td';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../slices/auth';
import { InboxAnnouncementTypes } from '../../../utils/constants';
import { dateAndTimeFormatter } from '../../../utils/dateFormatter';
import AreYouSure from '../../archived/routes/user/components/are-you-sure';
import { selectAnnouncements } from '../../../slices/inboxSlice';
import HowlingWolf from '../../../components/howling-wolf';
import { useSearchParams } from 'react-router-dom';

const OgmaDashboardInboxAnnouncement = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useSelector(selectUser);
  const announcements = useSelector(selectAnnouncements);
  const { isLoading } = useGetAllAnnouncementsQuery({});
  const [
    archiveAnnouncements,
    { data: archiveData, isLoading: archiveIsLoading, isSuccess: archiveIsSuccess },
  ] = useArchiveAnnouncementsMutation();
  const [checkedMessages, setCheckedMessages] = useState<Set<number>>(new Set());
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [isShowArchiveConfirmation, setIsShowArchiveConfirmation] = useState<boolean>(false);
  const [markAnnouncemensAsRead] = useMarkAnnouncementAsReadByMatchIdMutation();
  const messageColumns = [
    { displayName: 'Subject', property: 'subject' },
    { displayName: 'From', property: 'from' },
    { displayName: 'Date', property: 'time' },
  ];

  const params = Object.fromEntries(searchParams.entries());
  const [searchProperty, searchValue] = Object.entries(params)[0] || [null, null];

  const filteredAnnouncements =
    announcements?.filter((d: any) => {
      const userRead = d.Recipients.find((ar: any) => ar.userId === user.id);
      const isArchived = userRead?.isArchived ?? false;
      if (showArchived && !isArchived) return false;
      if (!showArchived && isArchived) return false;

      if (searchProperty && searchValue) {
        const value = searchValue.toLowerCase();

        if (searchProperty === 'subject') {
          if (!d.subject?.toLowerCase().includes(value)) return false;
        }
      }

      return true;
    }) ?? [];

  const allSelected =
    filteredAnnouncements.length > 0 && checkedMessages.size === filteredAnnouncements.length;

  const handleHeaderCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const all = new Set<number>(
        filteredAnnouncements.map((announcement: any) => announcement.id),
      );
      setCheckedMessages(all);
    } else {
      setCheckedMessages(new Set());
    }
  };

  const handleRowCheckboxChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setCheckedMessages((prev) => {
      const next = new Set(prev);
      if (e.target.checked) {
        next.add(index);
      } else {
        next.delete(index);
      }
      return next;
    });
  };

  return (
    <>
      <button
        className={`px-4 py-1 border-1 text-sm rounded cursor-pointer ${
          showArchived ? 'border-red-500' : 'border-gray-300'
        }`}
        onClick={() => {
          setCheckedMessages(new Set());
          setShowArchived(!showArchived);
        }}
      >
        Show Archived
      </button>
      <button
        onClick={() => {
          setIsShowArchiveConfirmation(true);
        }}
        className='btn red m-2'
        style={{ visibility: checkedMessages.size > 0 ? 'visible' : 'hidden' }}
      >
        {`Archive (${checkedMessages.size})`}
      </button>
      {filteredAnnouncements?.length !== 0 || isLoading ? (
        <TableContainer>
          <TableHead
            tableList={messageColumns.map((l) => l.displayName)}
            bool={false}
            checkBoxes={!showArchived}
            checked={allSelected}
            onCheck={handleHeaderCheckboxChange}
          />
          <tbody className='bg-white divide-y divide-gray-200'>
            {filteredAnnouncements
              ?.slice()
              ?.sort((a: any, b: any) => {
                return new Date(b.send_time).getTime() - new Date(a.send_time).getTime();
              })
              .map((d: any, i: number) => {
                return (
                  <tr
                    key={i}
                    onClick={() => {
                      markAnnouncemensAsRead({ id: +d.id });
                      navigate(`/match/${d.match_id}`);
                    }}
                  >
                    {messageColumns.map((o, j) => {
                      if (o.property === 'subject') {
                        return (
                          <TableTd key={j}>
                            <div
                              className={`flex items-center ${
                                showArchived ? 'justify-center' : ''
                              } text-gray-500 text-sm font-semibold`}
                            >
                              {!showArchived && (
                                <input
                                  type='checkbox'
                                  className='flex items-center justify-start mr-2'
                                  checked={checkedMessages.has(d.id)}
                                  onChange={handleRowCheckboxChange(d.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              )}
                              <div>
                                {d.subject}
                                <div className='flex justify-start text-xs text-gray-400'>
                                  {d.inbox_announcement_type ===
                                  InboxAnnouncementTypes.ForfeitNotice
                                    ? 'Forfeit Notice'
                                    : ''}
                                </div>
                              </div>
                              {d.Recipients?.find(
                                (ar: any) =>
                                  ar.userId === user.id &&
                                  new Date(ar.lastReadDate).getTime() <
                                    new Date(d.send_time).getTime(),
                              ) && <span className='ml-2 text-fen-blue text-xl'>&#8226;</span>}
                              &nbsp;
                              {(() => {
                                const chatroomUser = d.ChatroomUsers?.find(
                                  (cu: any) => cu.userId === user.id,
                                );
                                const lastRead = chatroomUser?.lastReadDate;

                                const unreadCount = d?.messages?.filter((m: any) => {
                                  if (!lastRead) return true;
                                  return new Date(m.time) > new Date(lastRead);
                                }).length;
                                return unreadCount && unreadCount > 0 ? (
                                  <span className='text-fen-red'>({unreadCount})</span>
                                ) : null;
                              })()}
                            </div>
                          </TableTd>
                        );
                      }
                      if (o.displayName === 'From') {
                        return (
                          <TableTd key={j}>
                            <div className='text-gray-500 text-sm whitespace-normal break-words'>
                              <div>Fenworks Admin</div>
                            </div>
                          </TableTd>
                        );
                      }
                      if (o.property === 'time') {
                        return (
                          <TableTd key={j}>
                            <div className='text-gray-500 text-sm'>
                              {dateAndTimeFormatter(d?.send_time).split(';')[0] + ''}
                            </div>
                          </TableTd>
                        );
                      }
                      return (
                        <TableTd key={j}>
                          <div className='text-gray-500 test-sm'>{d[o.property]}</div>
                        </TableTd>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </TableContainer>
      ) : (
        <div className='my-4'>
          <HowlingWolf text='No Announcements.' />
        </div>
      )}
      {isShowArchiveConfirmation && (
        <AreYouSure
          text={`archive ${checkedMessages.size} announcement${
            checkedMessages.size > 1 ? 's' : ''
          }`}
          handleCloseDeleteDialog={() => {
            setIsShowArchiveConfirmation(false);
          }}
          handleDelete={() => {
            archiveAnnouncements({ body: { announcementIds: [...checkedMessages] } });
            setCheckedMessages(new Set());
            setIsShowArchiveConfirmation(false);
          }}
          isLoading={archiveIsLoading}
          isSuccess={archiveIsSuccess}
        />
      )}
    </>
  );
};

export default OgmaDashboardInboxAnnouncement;
